import { memo, useCallback, useEffect, useRef } from 'react'

import { ShowcaseEntryEdit } from '@nativewaves/nw-cloud-sdk/showcase'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useCollectionShowcaseUpdateMutation } from 'hooks/mutations/showcase/Collection'
import { rawRoute } from 'pages/NWPlatform/Collections/Collection/Showcase'
import { collectionQueryKeys } from 'services/queryKeys'

const JSONRaw = () => {
  const { t } = useTranslation(['domain'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { collectionId, showcaseId } = rawRoute.useParams()

  const collectionShowcaseQuery = useQuery(collectionQueryKeys.showcaseDetail({ collectionId, showcaseId }))
  const { data: showcase } = collectionShowcaseQuery

  const { mutate: updateShowcase } = useCollectionShowcaseUpdateMutation()

  const handleFormSubmit = useCallback(
    (data: ShowcaseEntryEdit) => {
      updateShowcase(
        { collectionId, showcaseId, showcaseEntryEdit: data },
        {
          onSuccess: () => {
            enqueueSnackbar(t('domain:Showcase.Collection.collectionSaved'), {
              variant: 'success',
            })
          },
        },
      )
    },
    [collectionId, enqueueSnackbar, showcaseId, t, updateShowcase],
  )

  useEffect(() => {
    if (editorInstanceRef.current && collectionShowcaseQuery.isSuccess) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(showcase, null, 4))
    }
  }, [collectionShowcaseQuery.isSuccess, showcase])

  if (collectionShowcaseQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!showcase) {
    return <NotFound />
  }

  return (
    <Editor
      value={showcase}
      onSubmit={handleFormSubmit}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const JSON = memo(JSONRaw)
